import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Coins, RefreshCw, Zap, BarChart3, ListChecks } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";

const money4 = (n) => n == null ? "$0" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const compact = (n) => n == null ? "0" : Number(n).toLocaleString();

export default function AICostDashboardPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/admin/ai/usage-stats", { params: { days } });
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const byFeature = data?.by_feature || [];
  const byDay = data?.by_day || [];
  const recent = data?.recent || [];

  return (
    <div>
      <PageHeader
        eyebrow="AI Features"
        title="AI Cost Dashboard"
        subtitle="Every Claude call across the platform, priced at $3/M input + $15/M output tokens. Directional — actual usage-based billing lands in the Emergent Universal Key portal."
        actions={
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="border-white/10 h-9 w-32" data-testid="cost-window"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={load} disabled={busy} variant="outline" className="border-white/10" data-testid="cost-refresh">
              <RefreshCw className={"h-3 w-3 mr-1 " + (busy ? "animate-spin" : "")} /> Refresh
            </Button>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="cost-kpis">
        <Card className="border-white/10 bg-card p-4" data-testid="cost-kpi-calls">
          <div className="overline flex items-center gap-1"><ListChecks className="h-3 w-3" /> Total calls</div>
          <div className="text-2xl font-semibold font-display tabular-nums mt-1">{compact(totals.calls)}</div>
        </Card>
        <Card className="border-white/10 bg-card p-4" data-testid="cost-kpi-cost">
          <div className="overline flex items-center gap-1"><Coins className="h-3 w-3" /> Est. spend</div>
          <div className="text-2xl font-semibold font-display tabular-nums mt-1 text-emerald-300">{money4(totals.estimated_cost_usd)}</div>
        </Card>
        <Card className="border-white/10 bg-card p-4" data-testid="cost-kpi-latency">
          <div className="overline flex items-center gap-1"><Zap className="h-3 w-3" /> Avg latency</div>
          <div className="text-2xl font-semibold font-display tabular-nums mt-1">{totals.avg_latency_ms ? `${(totals.avg_latency_ms / 1000).toFixed(1)}s` : "—"}</div>
        </Card>
        <Card className="border-white/10 bg-card p-4" data-testid="cost-kpi-tokens">
          <div className="overline flex items-center gap-1"><BarChart3 className="h-3 w-3" /> Chars processed</div>
          <div className="text-2xl font-semibold font-display tabular-nums mt-1">{compact((totals.prompt_chars || 0) + (totals.response_chars || 0))}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5">≈ {compact(Math.round(((totals.prompt_chars || 0) + (totals.response_chars || 0)) / 4))} tokens</div>
        </Card>
      </div>

      {/* Per-day timeline */}
      {byDay.length > 0 ? (
        <Card className="border-white/10 bg-card p-5 mb-4" data-testid="cost-timeline">
          <div className="overline mb-3">Daily usage · calls vs cost</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={byDay}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="day" stroke="#71717a" fontSize={11} />
                <YAxis yAxisId="calls" stroke="#71717a" fontSize={11} />
                <YAxis yAxisId="cost" orientation="right" stroke="#71717a" fontSize={11} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Line yAxisId="calls" type="monotone" dataKey="calls" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} name="calls" />
                <Line yAxisId="cost" type="monotone" dataKey="estimated_cost_usd" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="cost ($)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      ) : (
        <Card className="border-white/10 bg-card p-8 mb-4 text-center text-sm text-zinc-500" data-testid="cost-empty">
          No AI calls logged in this window yet. Once analytics or ops-AI endpoints fire, they show up here.
        </Card>
      )}

      {/* Per-feature bar */}
      {byFeature.length > 0 && (
        <Card className="border-white/10 bg-card p-5 mb-4" data-testid="cost-per-feature">
          <div className="overline mb-3">Calls per feature</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byFeature} layout="vertical">
                <XAxis type="number" stroke="#71717a" fontSize={11} />
                <YAxis type="category" dataKey="feature" stroke="#71717a" fontSize={11} width={140} />
                <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Bar dataKey="calls" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
                <tr>
                  <th className="py-2 pr-3">Feature</th>
                  <th className="py-2 pr-3 text-right">Calls</th>
                  <th className="py-2 pr-3 text-right">Est. cost</th>
                  <th className="py-2 pr-3 text-right">Avg latency</th>
                </tr>
              </thead>
              <tbody>
                {byFeature.map((f) => (
                  <tr key={f.feature} className="border-b border-white/5" data-testid={`cost-feature-${f.feature}`}>
                    <td className="py-2 pr-3 font-medium">{f.feature}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{compact(f.calls)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-emerald-300">{money4(f.estimated_cost_usd)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{f.avg_latency_ms ? `${(f.avg_latency_ms / 1000).toFixed(1)}s` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Recent calls */}
      {recent.length > 0 && (
        <Card className="border-white/10 bg-card p-5" data-testid="cost-recent">
          <div className="overline mb-3">Recent 20 calls</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5">
                <tr>
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Feature</th>
                  <th className="py-2 pr-3 text-right">Prompt/Resp chars</th>
                  <th className="py-2 pr-3 text-right">Latency</th>
                  <th className="py-2 pr-3 text-right">Cost</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.id} className="border-b border-white/5" data-testid={`cost-recent-row-${r.id}`}>
                    <td className="py-2 pr-3 text-zinc-400">{new Date(r.at).toLocaleTimeString()}</td>
                    <td className="py-2 pr-3">{r.feature}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{compact(r.prompt_len)} · {compact(r.response_len)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{r.duration_ms ? `${r.duration_ms}ms` : "—"}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-emerald-300">{money4(r.estimated_cost_usd)}</td>
                    <td className="py-2 pr-3">
                      <Badge variant="outline" className={r.status === "ok" ? "border-emerald-500/40 text-emerald-300 text-[10px]" : "border-rose-500/40 text-rose-300 text-[10px]"}>{r.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
